import { isHolidayOrWeekend } from '@freightview/holidays'

import type { ShipmentLocation, ShipType } from '@fv/client-types'
import type { LocationAccessorialType, LocationType } from '@fv/models'
import { isFlatbedEquipment } from '@fv/models/equipment'

import { validateEmergencyContact } from '@/features/emergencyContact/contactFuncs'
import { getDestination, getOrigin } from '@/utils/shipmentFuncs'

import { loadCanBeBooked } from '../book/bookFuncs'
import type {
  BookEligibilityContext,
  BookEligibilityShipment,
  BookIneligibilityReason,
} from './types'

type HazmatDetail = {
  hazmatId?: string
  primaryClass?: string
  properShippingName?: string
}

type LocationContactPhoneArg = {
  type: LocationType
  shipType?: ShipType
  accessorials: LocationAccessorialType[]
}

export const loadLocationContactPhoneRequired = (
  location: LocationContactPhoneArg,
): boolean => {
  return (
    location.type === 'origin' ||
    location.shipType === 'residential' ||
    location.accessorials.includes('notify') ||
    location.accessorials.includes('schedule')
  )
}

function loadMissingContactPhone(
  locations: Pick<
    ShipmentLocation,
    'type' | 'shipType' | 'accessorials' | 'contactPhone'
  >[],
): boolean {
  return locations.some(
    loc =>
      loadLocationContactPhoneRequired({
        ...loc,
        accessorials: loc.accessorials.map(a => a.key),
      }) && !loc.contactPhone,
  )
}

function hasLocationRefNum(location?: {
  refNums?: Array<{ value?: string }>
}): boolean {
  return !!location?.refNums?.[0]?.value?.trim()
}

function hasItemsEntered(load: Pick<BookEligibilityShipment, 'items'>) {
  return load.items.length > 0
}

function hasFlatbedDetails(load: BookEligibilityShipment) {
  const equipmentType = load.selectedQuote?.equipmentType
  if (!isFlatbedEquipment(equipmentType)) return true

  const hasLocDetails = load.locations.every(
    location => !!location.flatbedHandlingType,
  )
  const hasItemDetails = load.items.every(
    item => !!item.width && !!item.length && !!item.height,
  )
  return hasLocDetails && hasItemDetails
}

function hasDeclaredValue(load: {
  items: Array<{ declaredValue?: number | string }>
}) {
  return load.items.every(item => !!item.declaredValue)
}

function hasHazardousItems(
  load: Pick<BookEligibilityShipment, 'items'>,
): boolean {
  return load.items.some(
    item =>
      !!item.hazardous?.length ||
      item.contains?.some(commodity => commodity.hazardous?.length),
  )
}

function hasCompleteHazmatDetail(hazardous: HazmatDetail): boolean {
  const hazmatId = hazardous.hazmatId?.trim()
  return (
    !!hazmatId &&
    /^(UN|NA)\d{4}$/.test(hazmatId) &&
    !!hazardous.primaryClass?.trim() &&
    !!hazardous.properShippingName?.trim()
  )
}

function hasRequiredHazmatDetails(
  load: Pick<BookEligibilityShipment, 'items'>,
): boolean {
  for (const item of load.items) {
    const hazardousEntries = [
      ...(item.hazardous ?? []),
      ...(item.contains?.flatMap(commodity => commodity.hazardous ?? []) ?? []),
    ]

    for (const hazardous of hazardousEntries) {
      if (!hasCompleteHazmatDetail(hazardous)) {
        return false
      }
    }
  }

  return true
}

function isInternational(locations: Array<{ country?: string }>) {
  const originCountry = locations[0]?.country
  return !!locations.find(location => location.country !== originCountry)
}

function hasRequiredCustomsBroker(
  load: Pick<BookEligibilityShipment, 'customsBroker' | 'locations'>,
): boolean {
  if (!isInternational(load.locations)) return true
  return !!load.customsBroker?.length
}

function wantsSchedulePickup(
  load: Pick<BookEligibilityShipment, 'schedulePickup'>,
): boolean {
  return load.schedulePickup ?? true
}

function hasRequiredBolDetails(
  load: BookEligibilityShipment,
  requiresFullBOLDetails: boolean,
): boolean {
  const requiresDetails = requiresFullBOLDetails || !wantsSchedulePickup(load)
  if (!requiresDetails) return true
  if (!hasItemsEntered(load)) return false

  return load.locations.every(location => {
    return (
      !!location.address?.trim() &&
      !!location.company?.trim() &&
      !!location.contactName?.trim()
    )
  })
}

function getOriginStopDate(
  locations: BookEligibilityShipment['locations'],
): Date | undefined {
  const origin = getOrigin({ locations })
  return origin?.stopDate ? new Date(origin.stopDate) : undefined
}

function canSchedulePickupForLoad(
  load: BookEligibilityShipment,
  ctx: BookEligibilityContext,
): boolean {
  const selectedQuote = load.selectedQuote
  const carrierId = selectedQuote?.carrierId
  if (!carrierId) return false

  const pickupDate = getOriginStopDate(load.locations)

  if (ctx.publicSettings?.dispatchMethod === 'disabled') return false
  if (!pickupDate) return false

  return !isHolidayOrWeekend(pickupDate)
}

function hasBookPath(
  load: BookEligibilityShipment,
  ctx: BookEligibilityContext,
): boolean {
  const carrierId = load.selectedQuote?.carrierId
  if (!carrierId) return false

  if (!wantsSchedulePickup(load)) {
    return !!ctx.publicSettings?.allowBolOnly
  }

  return canSchedulePickupForLoad(load, ctx)
}

export function getBookIneligibilityReasons(
  load: BookEligibilityShipment,
  ctx: BookEligibilityContext,
): BookIneligibilityReason[] {
  const reasons: BookIneligibilityReason[] = []
  const origin = getOrigin(load)
  const destination = getDestination(load)

  if (!load.selectedQuote) {
    reasons.push('missing-selected-quote')
    return reasons
  }

  if (!loadCanBeBooked(load)) {
    reasons.push('not-bookable-status')
  }

  if (!origin?.opensAt || !origin?.closesAt) {
    reasons.push('missing-origin-hours')
  }

  if (
    load.workflow !== 'truckload' &&
    ctx.bookSettings.requireOriginEmail &&
    !origin?.contactEmail?.trim()
  ) {
    reasons.push('missing-origin-email')
  }

  if (loadMissingContactPhone(load.locations)) {
    reasons.push('missing-contact-phone')
  }

  if (!hasRequiredBolDetails(load, ctx.bookSettings.requiresFullBOLDetails)) {
    reasons.push('missing-bol-details')
  }

  if (ctx.bookSettings.requiresFlatbedDetails && !hasFlatbedDetails(load)) {
    reasons.push('missing-flatbed-details')
  }

  if (ctx.bookSettings.requiresDeclaredValue && !hasDeclaredValue(load)) {
    reasons.push('missing-declared-value')
  }

  if (hasHazardousItems(load)) {
    if (!validateEmergencyContact(load.emergencyContact)) {
      reasons.push('missing-emergency-contact')
    }

    if (!hasRequiredHazmatDetails(load)) {
      reasons.push('missing-hazmat-details')
    }
  }

  if (
    ctx.accountSettings.booking?.shipperNumberRequired &&
    !hasLocationRefNum(origin)
  ) {
    reasons.push('missing-shipper-number')
  }

  if (
    ctx.accountSettings.booking?.poNumberRequired &&
    !hasLocationRefNum(destination)
  ) {
    reasons.push('missing-po-number')
  }

  if (!hasRequiredCustomsBroker(load)) {
    reasons.push('missing-customs-broker')
  }

  if (
    ctx.accountSettings.glCode?.enabled &&
    ctx.accountSettings.glCode?.required &&
    !load.glCode
  ) {
    reasons.push('missing-gl-code')
  }

  if (!hasBookPath(load, ctx)) {
    reasons.push('no-book-path')
  }

  return reasons
}

export function getLoadBookEligibility(
  load: BookEligibilityShipment,
  ctx: BookEligibilityContext,
) {
  const reasons = getBookIneligibilityReasons(load, ctx)
  return { canBook: reasons.length === 0, reasons }
}
